<template>
    <div class="audio-library">
        <!-- 筛选按钮区域 -->
        <div class="status-filter-btns">
            <button v-for="item in statusOptions" :key="item.value"
                :class="['status-btn', { selected: statusFilter === item.value }]" @click="selectStatus(item.value)">
                {{ item.label }}
            </button>
        </div>

        <!-- 音频列表 -->
        <ul class="audio-list" v-infinite-scroll="loadMore" :infinite-scroll-disabled="isLoading || isNoMore"
            :infinite-scroll-distance="50">
            <li :class="['audio-item', { 'is-selected': item.isClicked }]" v-for="(item, i) in audioList" :key="i"
                @click="toggleSelect(item)" @mouseenter="item.isHovered = true" @mouseleave="item.isHovered = false">
                <div class="audio-container">
                    <!-- 音频播放图标 -->
                    <i class="el-icon-headset pointer" @click.stop="toggleAudioPlay(i)"
                        style="font-size: 50px; color: #999;"></i>

                    <!-- 音频名称 -->
                    <div class="audio-name">
                        <template v-if="item.isEditingName">
                            <input v-model="item.editingName" />
                            <div class="edit-btn-group">
                                <button class="edit-btn save" @click.stop="saveAudioName(item)">保存</button>
                                <button class="edit-btn cancel" @click.stop="cancelEditAudioName(item)">取消</button>
                            </div>
                        </template>
                        <template v-else>
                            <span class="audio-title-inline">
                                {{ item.originalName }}
                                <i class="el-icon-edit pointer" style="margin-left: 6px;"
                                    @click.stop="editAudioName(item)"></i>
                            </span>
                        </template>
                    </div>

                    <!-- 图标容器 -->
                    <div class="media-icons-container">
                        <!-- 标签图标 -->
                        <el-tooltip placement="top" effect="light" :content="getTagDescription(item)"
                            v-if="getIsShowTag(item)">
                            <div class="media-tag audio-tag">
                                <img :src="getTagIcon(item)" class="tag-icon" />
                            </div>
                        </el-tooltip>
                        <!-- 是否发送过 -->
                        <el-tooltip placement="top" effect="light" :content="'发送过'" v-if="item.casualStatus == 'sent'">
                            <div class="media-tagSec">
                                <i class="el-icon-s-promotion" style="color: #fff;font-size: 18px;"></i>
                            </div>
                        </el-tooltip>
                        <!-- 是否查看过 -->
                        <el-tooltip placement="top" effect="light" :content="'已听过'"
                            v-if="item.casualStatus == 'accessed'">
                            <div class="media-tagSec" style="background: rgba(255, 255, 255, 0.7) !important;">
                                <img src="@/assets/svg/listened.svg" style="width: 14px;">
                            </div>
                        </el-tooltip>
                        <!-- 重新审核按钮 -->
                        <el-tooltip placement="top" effect="light" :content="'重新审核'"
                            v-if="shouldShowReReview(item) && item.isHovered">
                            <div class="media-tagSec" @click="reReview(item)">
                                <i class="el-icon-refresh-right" style="color: #fff;font-size: 18px;"></i>
                            </div>
                        </el-tooltip>
                    </div>
                </div>

                <!-- 选中状态指示 -->
                <div class="selection-indicator" v-if="item.isClicked">
                    <i class="el-icon-success" style="color: #5FAAFF;"></i>
                </div>
            </li>

            <!-- 加载状态 -->
            <li v-if="isLoading" class="loading-item">
                <img class="loading-icon" src="@/assets/loading.gif" alt="">
            </li>

            <!-- 无更多数据 -->
            <li v-if="isNoMore && !isLoading" class="no-more-item">
                没有更多了
            </li>
        </ul>

        <!-- 音频播放弹窗 -->
        <el-dialog :visible.sync="audioPlayerShow" width="40%" :before-close="closeAudioPlayer" :modal="true"
            :close-on-click-modal="true" :modal-append-to-body="true" :append-to-body="true"
            custom-class="audio-player-modal" :title="currentAudioData?.originalName || '音频播放'">
            <div class="audio-player-dialog" v-if="currentAudioData">
                <div class="audio-container">
                    <div class="audio-icon">
                        <i class="el-icon-headset" style="font-size: 80px; color: #2499FF; margin-bottom: 20px;"></i>
                    </div>
                    <audio ref="dialogAudioPlayer" controls autoplay :src="currentAudioData.url"
                        @loadeddata="playAudioInDialog">
                        <source :src="currentAudioData.url" type="audio/mpeg">
                        <source :src="currentAudioData.url" type="audio/mp3">
                        您的浏览器不支持音频播放
                    </audio>
                </div>
            </div>
        </el-dialog>
    </div>
</template>

<script>
import { getGalleryAudios, sendForModeration, updateAudioName } from '@/api/talkytimes/message'
import { requestPDServer } from '@/utils/fetchResponse'

export default {
    name: 'ManagementAudioLibrary',
    props: {
        chatUserInfo: {
            type: Object,
            default: () => ({})
        }
    },
    data() {
        return {
            audioList: [],
            statusFilter: 'all',
            statusOptions: [
                { label: 'All', value: 'all' },
                { label: 'Approved', value: 'approved' },
                { label: 'On moderation', value: 'on_moderation' },
                { label: 'Declined', value: 'declined' },
            ],
            limit: 50,
            cursor: '',
            isLoading: false,
            isNoMore: false,
            hasError: false,
            audioPlayerShow: false,
            currentAudioData: null,
            currentPlayingIndex: -1
        }
    },
    watch: {
        statusFilter: {
            handler() {
                this.refresh()
            }
        },
        chatUserInfo: {
            handler(newVal, oldVal) {
                if (newVal && oldVal && newVal.userId !== oldVal.userId) {
                    this.refresh()
                }
            },
            deep: true
        }
    },
    mounted() {
        this.loadAudios()
    },
    methods: {
        async loadAudios(isLoadMore = false) {
            if (this.isLoading) return
            if (this.hasError && !isLoadMore) return

            this.isLoading = true
            this.hasError = false

            const idUser = this.chatUserInfo.userId || this.chatUserInfo.idInterlocutor || this.chatUserInfo.id_user || this.chatUserInfo.id

            if (!idUser) {
                console.error('无法获取有效的用户ID')
                this.isLoading = false
                this.hasError = true
                return
            }

            const params = {
                cursor: isLoadMore ? String(this.cursor) : "",
                statuses: [],
                limit: this.limit,
                idUser: idUser
            }

            // 根据筛选条件设置参数
            if (this.statusFilter === 'all') {
                params.statuses = []
            } else if (this.statusFilter === 'approved') {
                params.statuses = ['approved']
            } else if (this.statusFilter === 'on_moderation') {
                params.statuses = ['on_moderation']
            } else if (this.statusFilter === 'declined') {
                params.statuses = ['declined']
            }

            const res = await getGalleryAudios(params)

            const audioArr = Array.isArray(res.items) ? res.items : []
            const newAudios = audioArr.map(item => ({
                ...item,
                url: item.urls?.mp3 || item.url,
                originalName: item.title || '音频',
                fileCateType: 3,
                isClicked: false,
                isHovered: false,
                tags: item.tags || [],
                status: typeof item.status === 'string' ? { code: item.status } : item.status,
                casualStatus: item.casualStatus,
                isEditingName: false,
                editingName: ''
            }))

            if (isLoadMore) {
                this.audioList = this.audioList.concat(newAudios)
            } else {
                this.audioList = newAudios
            }

            this.cursor = audioArr.length > 0 ? (audioArr[audioArr.length - 1].id || '') : ''
            this.isNoMore = newAudios.length < this.limit

            this.isLoading = false
        },

        loadMore() {
            if (!this.isLoading && !this.isNoMore && !this.hasError) {
                this.loadAudios(true)
            }
        },

        refresh() {
            this.cursor = ''
            this.isNoMore = false
            this.hasError = false
            this.audioList = []
            this.loadAudios()
        },

        toggleSelect(item) {
            if (!this.canSelect(item)) return

            this.$set(item, 'isClicked', !item.isClicked)

            if (item.isClicked) {
                this.$emit('item-selected', item)
            } else {
                this.$emit('item-deselected', item)
            }
        },

        canSelect(item) {
            return true // 管理模式下都可以选择
        },

        selectStatus(value) {
            this.statusFilter = value
        },

        toggleAudioPlay(index) {
            if (this.currentPlayingIndex === index && this.audioPlayerShow) {
                this.closeAudioPlayer()
            } else {
                this.currentPlayingIndex = index
                this.currentAudioData = this.audioList[index]
                this.audioPlayerShow = true
            }
        },

        closeAudioPlayer() {
            this.audioPlayerShow = false
            if (this.$refs.dialogAudioPlayer) {
                this.$refs.dialogAudioPlayer.pause()
                this.$refs.dialogAudioPlayer.currentTime = 0
            }
            this.currentPlayingIndex = -1
            this.currentAudioData = null
        },

        playAudioInDialog() {
            if (this.$refs.dialogAudioPlayer) {
                this.$refs.dialogAudioPlayer.play().catch(err => {
                    console.error('播放音频失败:', err)
                })
            }
        },

        editAudioName(item) {
            this.$set(item, 'isEditingName', true)
            this.$set(item, 'editingName', item.originalName)
        },

        cancelEditAudioName(item) {
            this.$set(item, 'isEditingName', false)
            this.$set(item, 'editingName', '')
        },

        async saveAudioName(item) {
            if (!item.editingName || item.editingName.trim() === '') {
                this.$message.warning('音频名称不能为空')
                return
            }

            const idAudio = item.id || item.idAudio
            await updateAudioName({
                idAudio: idAudio,
                title: item.editingName.trim()
            })

            item.originalName = item.editingName.trim()
            item.isEditingName = false
            item.editingName = ''
            this.$message.success('修改成功')
        },

        getIsShowTag(item) {
            if (!item) return false
            const statusValue = typeof item.status === 'string' ? item.status : (item.status?.code || '')
            if (statusValue && statusValue.includes('approved')) {
                return item.tags && Array.isArray(item.tags) && item.tags.length > 0
            }
            return false
        },

        getTagIcon(item) {
            if (!item) return ''
            const statusValue = typeof item.status === 'string' ? item.status : (item.status?.code || '')
            if (statusValue && statusValue.includes('approved')) {
                if (item.tags && Array.isArray(item.tags) && item.tags.length > 0) {
                    const tagCode = typeof item.tags[0] === 'string' ? item.tags[0] : (item.tags[0]?.code || '')
                    switch (tagCode) {
                        case 'special_plus':
                            return require('@/assets/svg/special+.svg')
                        case 'pre_special':
                            return require('@/assets/svg/special-.svg')
                        case 'special':
                            return require('@/assets/svg/special.svg')
                        default:
                            return ''
                    }
                }
            } else if (statusValue && statusValue.includes('declined')) {
                return require('@/assets/svg/negative.svg')
            } else if (statusValue === 'on_moderation') {
                return require('@/assets/svg/clock.svg')
            }
            return ''
        },

        getTagDescription(item) {
            if (!item) return ''
            const statusValue = typeof item.status === 'string' ? item.status : (item.status?.code || '')
            if (statusValue && statusValue.includes('approved')) {
                if (item.tags && Array.isArray(item.tags) && item.tags.length > 0) {
                    const tagCode = typeof item.tags[0] === 'string' ? item.tags[0] : (item.tags[0]?.code || '')
                    switch (tagCode) {
                        case 'special_plus':
                            return '特殊+内容'
                        case 'pre_special':
                            return 'AI预设'
                        case 'special':
                            return '特殊内容'
                        default:
                            return ''
                    }
                }
            } else if (statusValue && statusValue.includes('declined')) {
                return '审核不通过'
            } else if (statusValue === 'on_moderation') {
                return '审核中'
            }
            return ''
        },

        shouldShowReReview(item) {
            if (!item) return false
            const statusValue = typeof item.status === 'string' ? item.status : (item.status?.code || '')
            if (!statusValue) return false
            if (statusValue.includes('declined')) return true
            if (statusValue === 'on_moderation') return true
            return false
        },

        async reReview(item) {
            const idAudio = item.id || item.idAudio
            await sendForModeration({ idAudio: idAudio })
            this.$message.success('重新提审成功')
            this.refresh()
        },

        async deleteItems(items) {
            if (!items || items.length === 0) return

            const itemIds = items.map(item => item.id || item.idAudio)
            // 使用 requestPDServer 调用删除音频接口
            await requestPDServer('/platform/gallery/audio/delete', { idsAudios: itemIds })

            this.audioList = this.audioList.filter(audio => {
                const audioId = audio.id || audio.idAudio
                return !itemIds.includes(audioId)
            })

            this.$message.success(`成功删除 ${items.length} 个音频`)
        }
    }
}
</script>

<style lang="scss" scoped>
.audio-library {
    height: 100%;
    display: flex;
    flex-direction: column;
}

.status-filter-btns {
    display: flex;
    gap: 16px;
    margin: 16px 0;
    padding: 0 8px;

    .status-btn {
        padding: 4px 16px;
        border: none;
        border-radius: 16px;
        background: #f7f9fa;
        color: #333;
        font-size: 15px;
        font-weight: 500;
        cursor: pointer;
        transition: background 0.2s, color 0.2s;

        &.selected {
            background: #eef2ff;
            color: #2346d4;
        }
    }
}

.audio-list {
    flex: 1;
    height: 100%;
    width: 100%;
    display: block;
    padding: 0;
    margin: 0;
    list-style: none;
    overflow-y: auto;

    .audio-item {
        position: relative;
        display: flex;
        align-items: center;
        padding: 10px;
        margin: 5px 0;
        border: 1px solid #e4e7ed;
        border-radius: 8px;
        cursor: pointer;
        transition: all 0.2s;

        &.is-selected {
            border: 1px solid #2499FF;
            background: #f0f8ff;
        }

        .audio-container {
            display: flex;
            align-items: center;
            width: 100%;
            position: relative;

            .audio-name {
                flex: 1;
                margin-left: 15px;

                input {
                    padding: 4px 8px;
                    border: 1px solid #dcdfe6;
                    border-radius: 4px;
                    width: 200px;
                }

                .edit-btn-group {
                    display: inline-block;
                    margin-left: 10px;

                    .edit-btn {
                        padding: 4px 12px;
                        margin-right: 5px;
                        border: none;
                        border-radius: 4px;
                        cursor: pointer;

                        &.save {
                            background: #67c23a;
                            color: #fff;
                        }

                        &.cancel {
                            background: #909399;
                            color: #fff;
                        }
                    }
                }

                .audio-title-inline {
                    display: inline-flex;
                    align-items: center;
                }
            }

            .media-icons-container {
                position: absolute;
                right: 0;
                display: flex;
                align-items: center;
                gap: 4px;
                z-index: 10;
            }
        }

        .media-tag,
        .media-tagSec {
            position: relative;
            background: rgba(20, 20, 20, 0.4);
            color: white;
            width: 20px;
            height: 20px;
            border-radius: 50px;
            display: flex;
            align-items: center;
            justify-content: center;
            cursor: pointer;

            .tag-icon {
                width: 18px;
                height: 18px;
            }
        }

        .selection-indicator {
            position: absolute;
            right: 10px;
            top: 10px;
        }
    }

    .loading-item,
    .no-more-item {
        display: flex;
        justify-content: center;
        align-items: center;
        height: 40px;
        width: 100%;
        color: #999;
        font-size: 14px;

        .loading-icon {
            width: 30px;
            height: 30px;
            animation: rotate360 1s linear infinite;
        }
    }
}

.audio-player-dialog {
    text-align: center;

    .audio-container {
        display: flex;
        flex-direction: column;
        align-items: center;

        audio {
            width: 100%;
            margin-top: 20px;
        }
    }
}

@keyframes rotate360 {
    from {
        transform: rotate(0deg);
    }
    to {
        transform: rotate(360deg);
    }
}
</style>
